import React, { useMemo } from 'react';
import { useMatch } from 'react-router-dom';
import { get, isEmpty } from 'lodash';
import presetStore from '@globalStore/presetsStore';
import {
  CURRENT_PRODUCT_MODE,
  PRESET_DASHBOARD_ROUTE_PATH,
  FLOW_PRESET_DASHBOARD_ROUTE_PATH,
  FRUSTRATION_SIGNAL_PRESET_DASHBOARD_ROUTE_PATH,
  EVENT_PRESET_DASHBOARD_ROUTE_PATH,
} from '@utils/constants';
import Main from '@src/pages/main/Main';
import AppMain from '@src/pages/AppExperience/index';
import PageNotFound from '@src/components/common/PageNotFound/index';
import { WithPersistence } from '@src/pages/index';
import {
  getAppFlowUsecaseKeyFromFlowId,
  getAppFrustrationSignalUsecaseKeyFromSignalId,
  getAppEventUsecaseKeyFromEventId,
} from '@utils/preset';
import initStore from '@src/store/common/initTrendsBasedStore';

const RenderPage = {
  EI: Main,
  AI: Main,
  APP: AppMain,
}[CURRENT_PRODUCT_MODE];

const PresetDashboardPage = (props) => {
  const flowPresetMatchedKey = useMatch(FLOW_PRESET_DASHBOARD_ROUTE_PATH)?.params.id;
  const frustrationSignalPresetMatchedKey = useMatch(FRUSTRATION_SIGNAL_PRESET_DASHBOARD_ROUTE_PATH)
    ?.params.id;
  const eventPresetMatchedKey = useMatch(EVENT_PRESET_DASHBOARD_ROUTE_PATH)?.params.id;
  const presetMatchedKey = useMatch(PRESET_DASHBOARD_ROUTE_PATH)?.params.id;
  const presetKey: string | undefined =
    flowPresetMatchedKey ??
    frustrationSignalPresetMatchedKey ??
    eventPresetMatchedKey ??
    presetMatchedKey;

  let preset = presetStore.getState().getPreset(presetKey);
  const isFlowPreset = !!flowPresetMatchedKey;
  const isFrustrationSignalPreset = !!frustrationSignalPresetMatchedKey;
  const isEventPreset = !!eventPresetMatchedKey;

  // For event presets, create a dynamic preset if it doesn't exist
  if (isEventPreset && !preset) {
    // Decode the event ID for display (e.g., "app%20crash" -> "app crash")
    const decodedEventId = presetKey ? decodeURIComponent(presetKey) : presetKey;
    preset = {
      title: decodedEventId,
      key: presetKey,
      config: {
        metricsChartContainer: {
          metric: 'AppTotalEvents',
          metrics: ['AppTotalEvents'],
        },
        common: {
          appliedFilters: [],
          granularity: 'PT5M',
        },
        'module:trends': {
          base: {
            dimensions: ['page_urlpath', 'page_title', 'device_name'],
          },
        },
      },
    };
  }

  const isValidPreset = !isEmpty(preset) && !preset.hide;
  const presetConfig = get(preset, 'config', {});

  // For flow presets, convert to usecaseKey format (flow:ID)
  // For frustration signal presets, convert to usecaseKey format (frustration-signal:ID)
  // For event presets, convert to usecaseKey format (event:ID)
  // For regular presets, use the key as-is
  const usecaseKey = isFlowPreset
    ? getAppFlowUsecaseKeyFromFlowId(presetKey || '')
    : isFrustrationSignalPreset
      ? getAppFrustrationSignalUsecaseKeyFromSignalId(presetKey || '')
      : isEventPreset
        ? getAppEventUsecaseKeyFromEventId(presetKey || '')
        : presetKey;

  // initialize jotai store with default state
  const defaultStore = useMemo(
    () => initStore({ ...presetConfig, usecaseKey }),
    [presetConfig, usecaseKey],
  );

  if (!isValidPreset) return <PageNotFound />;

  let title = presetConfig?.title || `Preset: ${preset?.title}`;

  if (isFlowPreset) {
    title = `Flow: ${preset?.title || presetConfig?.title || flowPresetMatchedKey}`;
  } else if (isFrustrationSignalPreset) {
    title = `Frustration Signal: ${preset?.title || presetConfig?.title || frustrationSignalPresetMatchedKey}`;
  } else if (isEventPreset) {
    // Decode the event ID for display (e.g., "app%20crash" -> "app crash")
    const decodedEventId = eventPresetMatchedKey
      ? decodeURIComponent(eventPresetMatchedKey)
      : eventPresetMatchedKey;
    title = `Event: ${preset?.title || presetConfig?.title || decodedEventId}`;
  }

  return (
    <WithPersistence key={presetKey}>
      <RenderPage {...props} title={title} store={defaultStore} />
    </WithPersistence>
  );
};

export default PresetDashboardPage;
